import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { haversineKm } from "../utils/gis";
import "./ORAOverlayCard.css";

const POLL_MS = 15_000;

const TREND_LABEL = {
  intensifying: "Intensifying ▲",
  weakening:    "Weakening ▼",
  steady:       "Steady",
  unknown:      "Unknown",
};

const TREND_CLS = {
  intensifying: "ora-trend--warn",
  weakening:    "ora-trend--ok",
  steady:       "ora-trend--neutral",
  unknown:      "ora-trend--muted",
};

// ── Warning severity helpers ──────────────────────────────────────────────────

const WARN_RANK = [
  ["tornado emergency",           0],
  ["tornado warning",             1],
  ["flash flood emergency",       2],
  ["severe thunderstorm warning", 3],
  ["flash flood warning",         4],
  ["snow squall warning",         5],
  ["tornado watch",               6],
  ["severe thunderstorm watch",   7],
  ["flash flood watch",           8],
  ["special marine warning",      9],
];

function warnSortRank(w) {
  const t = (w.type || w.event || w.title || "").toLowerCase();
  for (const [k, r] of WARN_RANK) { if (t.includes(k)) return r; }
  const sev = (w.severity || "").toLowerCase();
  if (sev === "extreme") return 10;
  if (sev === "severe")  return 11;
  if (sev === "moderate") return 12;
  return 13;
}

function warnDisplayClass(w) {
  const t = (w.type || w.event || w.title || "").toLowerCase();
  if (t.includes("tornado emergency") || t.includes("tornado warning")) return "tornado";
  if (t.includes("severe thunderstorm warning")) return "severe-thunder";
  if (t.includes("flash flood emergency") || t.includes("flash flood warning")) return "flash-flood";
  if (t.includes("tornado watch"))             return "tornado-watch";
  if (t.includes("severe thunderstorm watch")) return "severe-watch";
  if (t.includes("flash flood watch"))         return "flood-watch";
  const sev = (w.severity || "").toLowerCase();
  if (sev === "extreme") return "extreme";
  if (sev === "severe")  return "severe";
  return "moderate";
}

function warnCenter(w) {
  if (typeof w.lat === "number" && typeof w.lon === "number") return [w.lon, w.lat];
  if (w.centroid) {
    const c   = w.centroid;
    const lon = c.lon ?? c[0] ?? null;
    const lat = c.lat ?? c[1] ?? null;
    if (lon != null && lat != null) return [lon, lat];
  }
  if (w.geometry?.type === "Polygon" && w.geometry.coordinates?.[0]?.length) {
    const ring = w.geometry.coordinates[0];
    return [
      ring.reduce((s, c) => s + c[0], 0) / ring.length,
      ring.reduce((s, c) => s + c[1], 0) / ring.length,
    ];
  }
  return null;
}

// Shared warning row used in both Active and Nearby lists
function WarningRow({ w, i, onWarningFocus }) {
  const wType    = w.type || w.title || w.event || "Warning";
  const wArea    = w.area || w.areaDesc || "";
  const wExpires = w.expires || "";
  const cls      = warnDisplayClass(w);
  const clickable = !!onWarningFocus;
  return (
    <button
      key={w.id || `${wType}-${wExpires}-${i}`}
      type="button"
      className={`sa-ora-warning-item sa-ora-warning-item--${cls}${clickable ? " sa-ora-warning-item--clickable" : ""}`}
      onClick={() => onWarningFocus?.(w)}
      title={clickable ? "Go to location" : undefined}
    >
      <span className="sa-ora-warning-type">{wType}</span>
      <span className="sa-ora-warning-meta">
        {wArea    && <span className="sa-ora-warning-area">{wArea}</span>}
        {wExpires && <span className="sa-ora-warning-expires">Exp {wExpires}</span>}
      </span>
    </button>
  );
}

/**
 * ORAOverlayCard — compact top-right glass overlay showing the latest O.R.A. analysis.
 *
 * Props:
 *   apiBase          {string}   – API root URL
 *   site             {string}   – active site (ICAO)
 *   frameCount       {number}   – current frame count (drives "no data" state)
 *   onWarningFocus   {function} – called with warning object when user clicks a warning row
 *   nearbyCenter     {object}   – { lat, lon, label, source } for nearby filtering; null = no selection
 *   onWarningsUpdate {function} – called with the full warnings array whenever it changes
 */
// eslint-disable-next-line no-unused-vars
export default function ORAOverlayCard({ apiBase, site, frameCount, onWarningFocus, nearbyCenter, onWarningsUpdate }) {
  const [status, setStatus]                 = useState(null);
  const [fetchErr, setFetchErr]             = useState(null);
  const [collapsed, setCollapsed]           = useState(true);
  const [isWarningsOpen, setIsWarningsOpen] = useState(false);
  const [isNearbyOpen, setIsNearbyOpen]     = useState(false);
  const abortRef                            = useRef(null);
  const fetchingRef                         = useRef(false);

  const fetchStatus = useCallback(async () => {
    if (fetchingRef.current) return;
    abortRef.current?.abort();
    const ctrl       = new AbortController();
    abortRef.current = ctrl;
    fetchingRef.current = true;

    try {
      const res = await fetch(`${apiBase}/ora/status`, {
        signal: ctrl.signal,
        cache:  "no-store",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setStatus(await res.json());
      setFetchErr(null);
    } catch (err) {
      if (err.name === "AbortError") return;
      setFetchErr(true);
    } finally {
      if (!ctrl.signal.aborted) fetchingRef.current = false;
    }
  }, [apiBase]);

  useEffect(() => {
    fetchStatus();
    const id = setInterval(fetchStatus, POLL_MS);
    return () => {
      clearInterval(id);
      abortRef.current?.abort();
    };
  }, [fetchStatus]);

  const analysis  = status?.last_analysis ?? null;
  const confPct   = analysis ? Math.round(analysis.confidence * 100) : 0;
  const confCls   = confPct < 40 ? "ora-conf-fill--low"
                  : confPct < 70 ? "ora-conf-fill--mid"
                  :                "ora-conf-fill--high";

  const activeWarnings = useMemo(
    () => (Array.isArray(status?.warnings) ? status.warnings : []),
    [status?.warnings],
  );

  useEffect(() => {
    onWarningsUpdate?.(activeWarnings);
  }, [activeWarnings, onWarningsUpdate]);

  // Sort by severity rank (lowest rank = highest priority)
  const sortedWarnings = useMemo(
    () => activeWarnings.slice().sort((a, b) => warnSortRank(a) - warnSortRank(b)),
    [activeWarnings],
  );

  // Nearby: only include warnings with a resolvable location AND within 350 km.
  // No-location warnings are excluded — section must stay geographically meaningful.
  const nearbyWarnings = useMemo(() => {
    if (!nearbyCenter) return [];
    return sortedWarnings.filter((w) => {
      const c = warnCenter(w);
      if (!c) return false; // no location — exclude from geographic filter
      return haversineKm(nearbyCenter.lon, nearbyCenter.lat, c[0], c[1]) <= 350;
    });
  }, [sortedWarnings, nearbyCenter]);

  return (
    <div className={`ora-card${collapsed ? " ora-card--collapsed" : ""}`}>

      {/* ── Header (always visible) ── */}
      <button
        className="ora-header"
        onClick={() => setCollapsed(c => !c)}
        aria-expanded={!collapsed}
        aria-label={collapsed ? "Expand O.R.A. panel" : "Collapse O.R.A. panel"}
      >
        <span className="ora-header-top">
          <span className="ora-label">O.R.A.</span>
          <span className="ora-classified">CLASSIFIED</span>

          {analysis && (
            <span className={`ora-trend ${TREND_CLS[analysis.trend] ?? "ora-trend--muted"}`}>
              {TREND_LABEL[analysis.trend] ?? analysis.trend}
            </span>
          )}

          {!analysis && !fetchErr && status && (
            <span className="ora-trend ora-trend--muted">No analysis</span>
          )}

          <span className="ora-chevron" aria-hidden="true">
            {collapsed ? "▸" : "▾"}
          </span>
        </span>
        {!collapsed && <span className="ora-subtitle">Operational Radar Analysis</span>}
      </button>

      {/* ── Body (collapsible) ── */}
      <div className="ora-body" aria-hidden={collapsed}>

        {/* Error state */}
        {fetchErr && (
          <p className="ora-placeholder">⚠ Unable to reach O.R.A.</p>
        )}

        {/* Loading state */}
        {!fetchErr && !status && (
          <p className="ora-placeholder">Connecting…</p>
        )}

        {/* No analysis cached */}
        {!fetchErr && status && !analysis && (
          <p className="ora-placeholder">
            No analysis yet.<br />
            Run one from <strong>O.R.A. Control</strong>.
          </p>
        )}

        {/* Analysis result */}
        {analysis && (
          <>
            <p className="ora-summary">{analysis.summary}</p>

            <div className="ora-conf-row">
              <span className="ora-conf-label">Confidence</span>
              <div className="ora-conf-bar">
                <div className={`ora-conf-fill ${confCls}`} style={{ width: `${confPct}%` }} />
              </div>
              <span className="ora-conf-pct">{confPct}%</span>
            </div>

            {analysis.hazards?.length > 0 && (
              <div className="ora-hazards">
                {analysis.hazards.map(h => (
                  <span key={h} className="ora-chip">{h}</span>
                ))}
              </div>
            )}

            {analysis.recommendation && (
              <p className="ora-rec">{analysis.recommendation}</p>
            )}

            <p className="ora-ts">
              {new Date(analysis.generated_at).toLocaleTimeString([], {
                hour: "2-digit", minute: "2-digit", timeZoneName: "short",
              })}
            </p>
          </>
        )}

        {/* ── Active Warnings ── sorted by severity, clickable ── */}
        <div className="ora-divider" />
        <div className="sa-ora-warnings">
          <button
            type="button"
            className="sa-ora-warnings-toggle"
            onClick={() => setIsWarningsOpen((o) => !o)}
            aria-expanded={isWarningsOpen}
            aria-label="Toggle active warnings"
          >
            <span className="ora-section-label">ACTIVE WARNINGS</span>
            <span className={`sa-ora-warnings-count${sortedWarnings.length === 0 ? " sa-ora-warnings-count--zero" : ""}`}>{sortedWarnings.length}</span>
            <svg className="sa-ora-warnings-chevron" viewBox="0 0 10 10" fill="none" aria-hidden="true">
              {isWarningsOpen
                ? <path d="M1.5 6.5L5 3L8.5 6.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                : <path d="M1.5 3.5L5 7L8.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              }
            </svg>
          </button>
          {isWarningsOpen && (
            <div className="sa-ora-warnings-list">
              {sortedWarnings.length === 0 ? (
                <p className="ora-placeholder">No active warnings</p>
              ) : (
                sortedWarnings.map((w, i) => (
                  <WarningRow key={w.id || i} w={w} i={i} onWarningFocus={onWarningFocus} />
                ))
              )}
            </div>
          )}
        </div>

        {/* ── Nearby Warnings ── filtered by nearbyCenter (radar site or future GPS) ── */}
        <div className="ora-divider" />
        <div className="sa-ora-warnings">
          <button
            type="button"
            className="sa-ora-warnings-toggle"
            onClick={() => setIsNearbyOpen((o) => !o)}
            aria-expanded={isNearbyOpen}
            aria-label="Toggle nearby warnings"
          >
            <span className="ora-section-label">NEARBY WARNINGS</span>
            {nearbyCenter ? (
              <span className="sa-ora-nearby-label">{nearbyCenter.label}</span>
            ) : (
              <span className="sa-ora-nearby-label sa-ora-nearby-label--dim">—</span>
            )}
            <span className={`sa-ora-warnings-count${(!nearbyCenter || nearbyWarnings.length === 0) ? " sa-ora-warnings-count--zero" : ""}`}>
              {nearbyCenter ? nearbyWarnings.length : "—"}
            </span>
            <svg className="sa-ora-warnings-chevron" viewBox="0 0 10 10" fill="none" aria-hidden="true">
              {isNearbyOpen
                ? <path d="M1.5 6.5L5 3L8.5 6.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                : <path d="M1.5 3.5L5 7L8.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              }
            </svg>
          </button>
          {isNearbyOpen && (
            <div className="sa-ora-warnings-list">
              {!nearbyCenter ? (
                <p className="ora-placeholder">Select a radar site to see nearby warnings.</p>
              ) : nearbyWarnings.length === 0 ? (
                <p className="ora-placeholder">No warnings near {nearbyCenter.label}</p>
              ) : (
                nearbyWarnings.map((w, i) => (
                  <WarningRow key={w.id || i} w={w} i={i} onWarningFocus={onWarningFocus} />
                ))
              )}
            </div>
          )}
        </div>

        {/* ── Core Detections ── */}
        <div className="ora-divider" />
        <div className="ora-section">
          <div className="ora-section-hdr">
            <span className="ora-section-label">CORE DETECTIONS</span>
          </div>
          {[
            { name: "Rotation Detection",   status: "WATCHING" },
            { name: "Hook Echo Detection",  status: "WATCHING" },
            { name: "Debris Signature",     status: "INACTIVE" },
            { name: "Hail Core Detection",  status: "WATCHING" },
            { name: "Storm Classification", status: "READY"    },
          ].map(({ name, status }) => (
            <div className="ora-cap-row" key={name}>
              <span className={`ora-cap-dot ora-cap-dot--${status.toLowerCase()}`} />
              <span className="ora-cap-name">{name}</span>
              <span className={`ora-cap-status ora-cap-status--${status.toLowerCase()}`}>{status}</span>
            </div>
          ))}
        </div>

        {/* ── Active Alerts ── */}
        <div className="ora-divider" />
        <div className="ora-section">
          <div className="ora-section-hdr">
            <span className="ora-section-label">ACTIVE ALERTS</span>
            <span className="ora-section-badge">DEMO</span>
          </div>
          {[
            { text: "Supercell detected west of Dodge City", level: "high" },
            { text: "Rotation strengthening",                level: "high" },
            { text: "Large hail core detected",              level: "mid"  },
            { text: "Tornado potential increasing",          level: "high" },
          ].map(({ text, level }) => (
            <div className={`ora-alert ora-alert--${level}`} key={text}>
              <span className="ora-alert-dot" />
              <span className="ora-alert-text">{text}</span>
            </div>
          ))}
        </div>

        {/* ── AI Roadmap ── */}
        <div className="ora-divider" />
        <div className="ora-section ora-section--muted">
          <div className="ora-section-hdr">
            <span className="ora-section-label">AI ROADMAP</span>
            <span className="ora-section-badge ora-section-badge--future">COMING SOON</span>
          </div>
          {[
            "Tornado probability modeling",
            "Storm intensification prediction",
            "Storm path forecasting",
            "Mesocyclone lifecycle tracking",
          ].map((feat) => (
            <div className="ora-future-row" key={feat}>
              <span className="ora-future-dot" />
              <span className="ora-future-name">{feat}</span>
            </div>
          ))}
        </div>

      </div>
    </div>
  );
}
